// How long a WAV or AIFF is, read from its header alone.
//
// A stem-shaped seed (uploaded before per-clip audio) stores one full-track
// render per lane, from beat 0. When such a seed is opened in Audiotool, that
// render's length is what spans its lane — so it has to be known without
// decoding. Runs in the browser (viz) as well as Node, so DataView, not Buffer.
//
// WAV: frames = data bytes / (channels × bytes per sample), from `fmt ` and
// `data`, walking chunks (word-aligned; LIST and friends are skipped).
// AIFF/AIFC: COMM carries numSampleFrames and an 80-bit extended sample rate.

export function audioDurationSeconds(bytes: Uint8Array): number | null {
  if (bytes.length < 12) return null;
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const id = (o: number): string => String.fromCharCode(bytes[o]!, bytes[o + 1]!, bytes[o + 2]!, bytes[o + 3]!);
  const form = id(8);
  if (id(0) === "RIFF" && form === "WAVE") return wavDuration(v, id);
  if (id(0) === "FORM" && (form === "AIFF" || form === "AIFC")) return aiffDuration(v, id);
  return null;
}

function wavDuration(v: DataView, id: (o: number) => string): number | null {
  let rate = 0;
  let blockAlign = 0;
  for (let off = 12; off + 8 <= v.byteLength; ) {
    const chunk = id(off);
    const size = v.getUint32(off + 4, true);
    if (chunk === "fmt " && off + 8 + 16 <= v.byteLength) {
      const channels = v.getUint16(off + 10, true);
      rate = v.getUint32(off + 12, true);
      blockAlign = v.getUint16(off + 20, true) || channels * (v.getUint16(off + 22, true) / 8);
    } else if (chunk === "data") {
      if (!rate || !blockAlign) return null;
      // A streaming writer may leave the size unset; fall back to what's there.
      const bytes = size === 0xffffffff || off + 8 + size > v.byteLength ? v.byteLength - off - 8 : size;
      return Math.floor(bytes / blockAlign) / rate;
    }
    off += 8 + size + (size & 1);
  }
  return null;
}

function aiffDuration(v: DataView, id: (o: number) => string): number | null {
  for (let off = 12; off + 8 <= v.byteLength; ) {
    const chunk = id(off);
    const size = v.getUint32(off + 4);
    if (chunk === "COMM" && off + 8 + 18 <= v.byteLength) {
      const frames = v.getUint32(off + 10);
      const rate = extended80(v, off + 16);
      return rate > 0 ? frames / rate : null;
    }
    off += 8 + size + (size & 1);
  }
  return null;
}

/** 80-bit IEEE-754 extended, big-endian (AIFF's sample rate). */
function extended80(v: DataView, off: number): number {
  const exponent = v.getUint16(off) & 0x7fff;
  const mantissa = v.getUint32(off + 2) * 2 ** 32 + v.getUint32(off + 6);
  if (exponent === 0 && mantissa === 0) return 0;
  return mantissa * 2 ** (exponent - 16383 - 63);
}
